/**
 * MessageThread Component
 *
 * Displays messages in a conversation with automatic scrolling and real-time updates.
 * Shows sender info, timestamps, and read status.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-476 - Core Messaging Infrastructure (13 SP)
 * Location: apps/web/src/components/messages/MessageThread.jsx
 */

import React, { useEffect, useRef } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { MessageCircle } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import EmptyState from '../common/EmptyState';

/**
 * MessageThread Component
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of message objects
 * @param {string} props.currentUserId - Current user's ID
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 */
const MessageThread = ({
  messages = [],
  currentUserId,
  loading = false,
  error = null,
}) => {
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <LoadingSpinner size="md" color="border-rose-600" className="h-full" />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Alert variant="error" message={error} className="max-w-md" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState
          icon={MessageCircle}
          title="No messages yet"
          description="Start a conversation"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto p-4 space-y-4">
      {messages.map((message, index) => {
        const isOwnMessage = message.sender_id === currentUserId;
        const showDateDivider = shouldShowDateDivider(messages, index);

        return (
          <React.Fragment key={message.id}>
            {showDateDivider && (
              <DateDivider date={message.created_at} />
            )}
            <MessageBubble
              message={message}
              isOwnMessage={isOwnMessage}
            />
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};

/**
 * MessageBubble Component
 *
 * Individual message bubble
 */
const MessageBubble = ({ message, isOwnMessage }) => {
  const { message_text, created_at, is_read, channel } = message;

  // Format time
  const timeString = format(new Date(created_at), 'h:mm a');

  // Get channel badge
  const getChannelBadge = (channel) => {
    if (channel === 'email') {
      return (
        <span className="inline-flex items-center text-xs text-gray-500 ml-2">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
          via email
        </span>
      );
    }
    if (channel === 'sms') {
      return (
        <span className="inline-flex items-center text-xs text-gray-500 ml-2">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          via SMS
        </span>
      );
    }
    return null;
  };

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-xs md:max-w-md lg:max-w-lg ${isOwnMessage ? 'order-2' : 'order-1'}`}>
        <div
          className={`rounded-lg px-4 py-2 ${
            isOwnMessage
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-900'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">
            {message_text}
          </p>
        </div>
        <div className={`flex items-center mt-1 text-xs text-gray-500 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
          <span>{timeString}</span>
          {getChannelBadge(channel)}
          {isOwnMessage && (
            <span className="ml-2">
              {is_read ? (
                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * DateDivider Component
 *
 * Shows date separator between messages
 */
const DateDivider = ({ date }) => {
  const formatDate = (date) => {
    const d = new Date(date);
    if (isToday(d)) {return 'Today';}
    if (isYesterday(d)) {return 'Yesterday';}
    return format(d, 'MMMM d, yyyy');
  };

  return (
    <div className="flex items-center justify-center my-4">
      <div className="px-4 py-1 bg-gray-100 rounded-full text-xs text-gray-600 font-medium">
        {formatDate(date)}
      </div>
    </div>
  );
};

/**
 * Helper: Determine if date divider should be shown
 */
const shouldShowDateDivider = (messages, index) => {
  if (index === 0) {return true;}

  const currentDate = new Date(messages[index].created_at);
  const previousDate = new Date(messages[index - 1].created_at);

  // Show divider if dates are different
  return currentDate.toDateString() !== previousDate.toDateString();
};

export default MessageThread;
